import React, { useState, useEffect } from "react";
import FlipClockCountdown from "@leenguyen/react-flip-clock-countdown";
import "@leenguyen/react-flip-clock-countdown/dist/index.css";
import { useDispatch, useSelector } from "react-redux";
import { getTicketTimerData } from "../redux/slices/ticketTimerSlice";
import { useTranslation } from "react-i18next";

const Clock = ({ onFreeze, onCountdownStart }) => {
  const dispatch = useDispatch();
  const { timerData } = useSelector((state) => state.ticketTimer);

  const { t } = useTranslation();

  // Helper function to parse the time string into a Date object
  const parseTimeString = (slot) => {
    const [hours, minutes, seconds] = slot.split(",").map(Number);
    const currentTime = new Date();
    currentTime.setHours(hours, minutes, seconds);
    return currentTime;
  };

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0,
  });
  const [isListVisible, setIsListVisible] = useState(false);
  const [currentSlotTime, setCurrentSlotTime] = useState(null);

  // Fetch ticket timer data from the API when the component mounts
  useEffect(() => {
    const fetchTicketTimerData = async () => {
      try {
        await dispatch(getTicketTimerData()).unwrap();
      } catch (error) {
        console.error("Error fetching ticket timer data:", error);
      }
    };

    fetchTicketTimerData();
  }, [dispatch]);

  // Determine the next available slot from the fetched timer data
  // Corrected time slot data and sorted slots in ascending order
  useEffect(() => {
    const updateCurrentSlot = () => {
      if (timerData && timerData.data?.slots) {
        // Parse and sort slots in ascending order
        const slots = timerData.data.slots
          .map((slot) => parseTimeString(slot))
          .sort((a, b) => a - b);

        const now = new Date();

        // Find the next valid slot for today
        let nextSlot = slots.find((slot) => slot > now);

        // If no slots remain, restart from the first slot of the next day
        if (!nextSlot) {
          const firstSlot = new Date(slots[0]);
          firstSlot.setDate(now.getDate() + 1); // Move to the next day
          nextSlot = firstSlot;
        }

        setCurrentSlotTime(nextSlot);
      }
    };

    updateCurrentSlot();
  }, [timerData]);

  // Update countdown timer at one-second intervals
  useEffect(() => {
    if (!currentSlotTime) return;

    const interval = setInterval(() => {
      const updatedTimeLeft = getTimeUntil(currentSlotTime);
      setTimeLeft(updatedTimeLeft);

      // Freeze UI when time left reaches 15 minutes (900 seconds)
      if (updatedTimeLeft.totalSeconds <= 1800) {
        onFreeze(true);
      }

      // Trigger countdown start event at 10 seconds remaining
      if (updatedTimeLeft.totalSeconds < 11) {
        onCountdownStart(updatedTimeLeft.totalSeconds);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [onFreeze, onCountdownStart, currentSlotTime]);

  // Calculate remaining time until the next slot
  const getTimeUntil = (targetTimestamp) => {
    const now = new Date().getTime();
    const diff = targetTimestamp - now;

    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    return { hours, minutes, seconds, totalSeconds };
  };

  // Toggle visibility of the slot list
  const toggleListVisibility = () => {
    setIsListVisible((prevState) => !prevState);
  };

  return (
    <div className="flex justify-center items-center max-w-72 lg:max-w-full">
      <div className="relative mx-2 rounded-2xl bg-gradient-to-br from-yellow-800 via-yellow-300 to-yellow-800 p-3 text-center shadow-[10px]">
        <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 animate-pulse blur-[10px]"></div>

        <h2 className="mb-2 text-xl font-bold text-black sm:text-3xl lg:text-3xl text-shadow-xl">
          {t("jackpot_start_time")}
        </h2>

        {/* Countdown Timer */}
        <FlipClockCountdown
          to={new Date().getTime() + timeLeft.totalSeconds * 1000}
          className="flex justify-center mb-[-24px]"
          digitBlockStyle={{
            width: "25px",
            height: "35px",
            fontSize: "20px",
            fontWeight: "bold",
            backgroundColor: "black",
            color: "#facc15",
            borderRadius: "10px",
            border: "1px solid #f59e0b",
          }}
          separatorStyle={{
            color: "black",
            fontSize: "1rem",
            textShadow: "0 0 10px rgba(250, 204, 21, 0.8)",
          }}
          renderMap={[false, true, true, true]}
          labels={["", "", "", ""]}
        />

        <p className="text-sm text-black sm:text-sm font-bold">
          {t("ticket_window_closing")}
        </p>

        {/* Toggle Button for Slot List */}
        <div className="flex justify-center gap-1 items-center">
          <span className="text-md font-bold text-black animate-slideInFromLeft">
            {t("result_details")}
          </span>
          <button
            onClick={toggleListVisibility}
            className="text-xl text-black font-bold bg-transparent transition-transform transform hover:scale-110 focus:outline-none relative animate-bounce"
          >
            {isListVisible ? (
              <span className="transition-transform transform rotate-180">
                &#x2191;
              </span>
            ) : (
              <span className="transition-transform transform rotate-0">
                &#x2193;
              </span>
            )}
          </button>
        </div>

        {/* Slot List */}
        {isListVisible && (
          <ul className="m2-4 text-center font-bold animate-slideInFromTop">
            {timerData?.data?.slots
              .map((slot) => parseTimeString(slot))
              .sort((a, b) => a - b) // Sort slots in ascending order
              .map((slotTime, index) => {
                const isLatestSlot =
                  currentSlotTime &&
                  slotTime.getHours() === currentSlotTime.getHours() &&
                  slotTime.getMinutes() === currentSlotTime.getMinutes() &&
                  slotTime.getSeconds() === currentSlotTime.getSeconds();

                return (
                  <li
                    key={index}
                    className={`p-2 m-2 rounded-lg transition-all duration-500 ${
                      isLatestSlot
                        ? "bg-gradient-to-r from-[#1a1714] via-[#584c48] to-[#11100f] text-white shadow-lg transform scale-105"
                        : "bg-gray-200 text-black hover:bg-gray-300 hover:shadow-md"
                    }`}
                  >
                    {slotTime.toLocaleTimeString()}
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Clock;
